// Statistics: the manuscript's words against its target, words by chapter, this week's writing and the year grid,
// every figure in plain words beside its bar (as the Mac's Statistics cards show them).
import { useMemo, useState } from 'react';
import { averageRate, bestRate, sprintNet, sprintsIn, wordsPerMinute } from '@writing-suite/core';
import { useWorkspace } from '../shell/workspace.ts';
import { readSprintLog } from '../store/writing.ts';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { activityGrid, formatNumber, formatWords, localDay, week, writingYears } from './helpers.ts';
import { ActivityGrid } from './Home.tsx';
import { wordsOfPeople } from '../store/members.ts';
import { PersonDot } from './People.tsx';
import type { PersonWords } from '@writing-suite/core/members';
import { scriptSpeeches } from '@writing-suite/core/story';
import { fountainPageCount, multicamPageCount, stagePageCount } from '@writing-suite/compile/screenplay';
import { runningMinutes, runningPhrase } from '@writing-suite/compile';
import { songsByCharacter, type OpenedProject, type Project } from '@writing-suite/core';
import type { LiveProject } from '../store/project.ts';
import { historyDay } from '../store/importScrivener.ts';
import { ScrivenerHistoryDialog } from './plan/ScrivenerReimport.tsx';


function Bar({ value, max, label }: { value: number; max: number; label: string }) {
  const fraction = max > 0 ? Math.min(1, value / max) : 0;
  return (
    <span className="bar" role="img" aria-label={label}>
      <span className="bar-fill" ref={(el) => el?.style.setProperty('--fraction', String(fraction))} />
    </span>
  );
}

const SPRINT_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export function Statistics({ project, live }: PageProps) {
  const ws = useWorkspace();
  const total = useMemo(() => project.manuscriptWords(), [project]);
  const targets = useMemo(() => project.targets(), [project]);
  const chapters = useMemo(() => project.chapters(), [project]);
  const daily = useMemo(() => project.daily(), [project]);
  const thisWeek = week(daily);
  const today = daily.find((d) => d.day === localDay(new Date())) ?? { added: 0, removed: 0 };
  // The year card: the last 12 months, or a calendar year with writing (null = the last 12 months).
  const years = useMemo(() => writingYears(daily), [daily]);
  const [shownYear, setShownYear] = useState<number | null>(null);
  const year = shownYear !== null && years.includes(shownYear) ? shownYear : null;
  const grid = activityGrid(daily, targets.session, new Date(), year);
  const target = targets.manuscript;
  const most = Math.max(1, ...chapters.map((c) => c.words));
  const weekMost = Math.max(1, ...thisWeek.days.map((d) => d.words));

  return (
    <div className="stats-page">
      <header className="page-head">
        <h1 tabIndex={-1}>Statistics</h1>
        <p className="page-sub">{targets.countAllText ? 'Counting all text' : 'Counting the manuscript'}</p>
      </header>

      <section className="stat-card" aria-labelledby="stat-total">
        <h2 id="stat-total" className="eyebrow">
          Manuscript
        </h2>
        <p className="stat-figure" data-manuscript-words={total}>
          {formatWords(total)}
        </p>
        {target ? (
          <>
            <Bar value={total} max={target} label={`${Math.round((100 * total) / target)}% of the target`} />
            <p className="stat-note">
              {total >= target ? `Past the target of ${formatWords(target)}.` : `${formatNumber(target - total)} to go to ${formatWords(target)}.`}
            </p>
          </>
        ) : (
          <p className="stat-note">No word target set.</p>
        )}
      </section>

      {chapters.length ? (
        <section className="stat-card" aria-labelledby="stat-chapters">
          <h2 id="stat-chapters" className="eyebrow">
            Words by chapter
          </h2>
          <ol className="chapter-bars">
            {chapters.map((c) => (
              <li key={c.nodeId}>
                <span className="chapter-name">
                  {c.opener ? <span className="chapter-label">{c.opener.label}</span> : null}
                  <span>{c.title || 'Untitled'}</span>
                </span>
                <Bar value={c.words} max={most} label={formatWords(c.words)} />
                <span className="num">{formatNumber(c.words)}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <section className="stat-card" aria-labelledby="stat-week">
        <h2 id="stat-week" className="eyebrow">
          This week
        </h2>
        <p className="stat-figure small">{formatWords(thisWeek.total)}</p>
        <p className="stat-note">{thisWeek.sentence}</p>
        <p className="stat-note" data-today-added={today.added} data-today-removed={today.removed}>
          Today: {formatWords(today.added)} written{today.removed ? `, ${formatWords(today.removed)} cut` : ''}.
        </p>
        <ol className="week-bars">
          {thisWeek.days.map((d) => (
            <li key={d.day} title={`${d.label}: ${formatWords(d.words)}`}>
              <span className="week-bar" role="img" aria-label={`${d.label}: ${formatWords(d.words)}`}>
                <span className="week-fill" ref={(el) => el?.style.setProperty('--fraction', String(d.words / weekMost))} />
              </span>
              <span className="week-day" aria-hidden="true">
                {d.label.slice(0, 1)}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {project.type === 'screenplay' ? <Speeches project={project} /> : null}

      <Sprints projectId={project.id} version={ws?.writing?.state.log.length ?? 0} />

      <People project={project} live={live} version={ws?.writing?.state.log.length ?? 0} />

      <section className="stat-card" aria-labelledby="stat-year">
        <h2 id="stat-year" className="eyebrow">
          {year === null ? 'The last year' : year}
        </h2>
        {years.length ? <YearSwitcher years={years} year={year} onChange={setShownYear} /> : null}
        <p className="stat-note">
          {year === null
            ? grid.year ? `${formatWords(grid.year)} written in the last year.` : 'No writing recorded in the last year.'
            : `${formatWords(grid.year)} written in ${year}, on ${grid.days === 1 ? '1 day' : `${grid.days} days`}.`}{' '}
          {targets.session ? `Shaded by your daily target of ${formatWords(targets.session)}.` : 'Shaded by how much you wrote on your writing days.'}
        </p>
        <ActivityGrid daily={daily} target={targets.session} year={year} />
        <ImportedHistoryLine project={project} live={live} canWrite={!!live?.writable && ws?.role !== 'commenter'} />
      </section>
    </div>
  );
}

/** ‹ Older · the period, a menu of them all · Newer › — the last 12 months, then each year with writing, newest first
 *  (the Mac's Writing activity card). */
function YearSwitcher({ years, year, onChange }: { years: number[]; year: number | null; onChange: (year: number | null) => void }) {
  const periods: (number | null)[] = [null, ...years];
  const index = Math.max(0, periods.indexOf(year));
  return (
    <p className="year-switcher" data-year-switcher>
      <button type="button" className="icon-button" aria-label="Older" title="Older" disabled={index + 1 >= periods.length} onClick={() => onChange(periods[index + 1])}>
        ‹
      </button>
      <select aria-label="Show" value={year ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
        {periods.map((p) => (
          <option key={p ?? 'last'} value={p ?? ''}>
            {p ?? 'Last 12 months'}
          </option>
        ))}
      </select>
      <button type="button" className="icon-button" aria-label="Newer" title="Newer" disabled={index === 0} onClick={() => onChange(periods[index - 1])}>
        ›
      </button>
    </p>
  );
}

/** Under the year: the days that came from Scrivener's writing history, and a way to bring it in for a project imported
 *  before it came with the import, or one that came another way. */
function ImportedHistoryLine({ project, live, canWrite }: { project: Project; live: LiveProject | null; canWrite: boolean }) {
  const [open, setOpen] = useState(false);
  const days = useMemo(() => (project as OpenedProject).importedHistory?.() ?? [], [project]);
  if (!canWrite && !days.length) return null;
  return (
    <div className="imported-history" data-imported-days={days.length}>
      <p className="stat-note">
      {days.length ? (
        <>
          Includes {days.length === 1 ? '1 day' : `${days.length.toLocaleString('en-GB')} days`} written in Scrivener,{' '}
          {historyDay(days[0].day)} – {historyDay(days[days.length - 1].day)}.{' '}
        </>
      ) : null}
      {canWrite && live ? (
        <button type="button" className="link-button" onClick={() => setOpen(true)} data-import-history>
          {days.length ? 'Import Again…' : 'Import Scrivener History…'}
        </button>
      ) : null}
      </p>
      {open && live ? <ScrivenerHistoryDialog live={live} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

/** Statistics ▸ Sessions (the Mac's SprintsPanel): recent writing sessions on this browser and the rates across them. */
function Sprints({ projectId, version }: { projectId: string; version: number }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const log = useMemo(() => sprintsIn(readSprintLog(), projectId), [projectId, version]);
  const average = averageRate(log);
  const best = bestRate(log);
  return (
    <section className="stat-card" aria-labelledby="stat-sprints" data-sprints={log.length}>
      <h2 id="stat-sprints" className="eyebrow">
        Sessions
      </h2>
      {log.length === 0 ? (
        <p className="stat-note">No sessions yet on this computer. Start one from the footer in Write.</p>
      ) : (
        <>
          <p className="stat-note">
            {average !== null ? `Average ${formatNumber(Math.round(average))} words a minute.` : ''}
            {best ? ` Best ${formatNumber(Math.round(wordsPerMinute(best)))} (sessions of five minutes or more).` : ''}
          </p>
          <ol className="sprint-list">
            {log.slice(0, 5).map((s) => (
              <li key={s.id} data-sprint-added={s.added} data-sprint-removed={s.removed}>
                <span>{SPRINT_DATE.format(new Date(s.started))}</span>
                <span className="num">
                  +{formatNumber(s.added)} −{formatNumber(s.removed)} ({sprintNet(s) >= 0 ? '+' : '−'}
                  {formatNumber(Math.abs(sprintNet(s)))} net)
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

const LAST_DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/** Statistics ▸ By person (W9c, shared projects): each member's words written and cut and writing days, from the word
 *  logs' devices; writing sessions are kept on each writer's computer, so only yours (from this one) are counted. */
function People({ project, live, version }: Pick<PageProps, 'project' | 'live'> & { version: number }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const people = useMemo(() => wordsOfPeople(project, live), [project, live]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mySprints = useMemo(() => sprintsIn(readSprintLog(), project.id), [project.id, version]);
  if (!people) return null;
  const me = live?.memberId ?? null;
  const day = (p: PersonWords) => (p.lastDay ? LAST_DAY.format(new Date(`${p.lastDay}T12:00:00`)) : '—');
  return (
    <section className="stat-card" aria-labelledby="stat-people" data-people={people.length}>
      <h2 id="stat-people" className="eyebrow">
        By person
      </h2>
      <table className="people-table">
        <thead>
          <tr>
            <th scope="col">Writer</th>
            <th scope="col" className="num">Written</th>
            <th scope="col" className="num">Cut</th>
            <th scope="col" className="num" title="Days with writing">Days</th>
            <th scope="col" className="num optional">Last</th>
            <th scope="col" className="num optional">Sessions</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.id ?? 'unknown'} data-person={p.name} data-person-added={p.added} data-person-removed={p.removed} data-person-days={p.days}>
              <th scope="row">
                <PersonDot color={p.color} />
                {p.name}
                {p.id !== null && p.id === me ? ' (you)' : ''}
              </th>
              <td className="num">{formatNumber(p.added)}</td>
              <td className="num">{formatNumber(p.removed)}</td>
              <td className="num">{formatNumber(p.days)}</td>
              <td className="num optional">{day(p)}</td>
              <td className="num optional" data-person-sprints={p.id !== null && p.id === me ? mySprints.length : ''}>{p.id !== null && p.id === me ? formatNumber(mySprints.length) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="stat-note">A device’s words count for its writer once it has written in the shared project (before that they show as an unknown device); writing sessions stay on the computer they were run on, so only yours from this one are counted here.</p>
    </section>
  );
}

/** Statistics ▸ Speeches by character (the Mac's SpeechesPanel): a script's running time by its format (a UK play's and a
 *  radio play's pages estimated with the US layout here), each character's speeches and words of dialogue, most first,
 *  and a musical's songs by who sings them. */
function Speeches({ project }: { project: Project }) {
  const { pages, speeches, songs } = useMemo(() => {
    const texts = project.scenes(['screenplay']).flatMap(({ id }) => {
      const text = project.fragment(id)?.text;
      return text ? [text] : [];
    });
    const play = project.kind === 'stage-play' || project.kind === 'radio-play';
    const pages = project.scriptFormat === 'multicam' ? multicamPageCount(texts) : play ? stagePageCount(texts) : fountainPageCount(texts);
    return { pages, speeches: scriptSpeeches(texts), songs: songsByCharacter(texts) };
  }, [project]);
  const minutes = project.musical ? runningMinutes(pages, project.scriptFormat, songs.total) : runningMinutes(pages, project.scriptFormat);
  const most = Math.max(1, ...speeches.map((s) => s.words));
  const cast = project.entities?.() ?? [];
  const character = (name: string) => cast.find((e) => e.kind === 'character' && [e.name, ...e.aliases].some((n) => n.toUpperCase() === name));
  return (
    <section className="stat-card" aria-labelledby="stat-speeches" data-speeches={speeches.length}>
      <h2 id="stat-speeches" className="eyebrow">
        Speeches by character
      </h2>
      <p className="stat-note" data-pages={pages} data-minutes={minutes}>
        {project.musical
          ? `${formatNumber(pages)} ${pages === 1 ? 'page' : 'pages'} of book and ${formatNumber(songs.total)} ${songs.total === 1 ? 'song' : 'songs'}: roughly ${runningPhrase(minutes)}, at a page a minute and three and a half minutes a song.`
          : project.scriptFormat === 'multicam'
            ? `About ${formatNumber(pages)} ${pages === 1 ? 'page' : 'pages'}: roughly ${runningPhrase(minutes)}, at about thirty seconds a page.`
            : `About ${formatNumber(pages)} ${pages === 1 ? 'page' : 'pages'}: roughly ${runningPhrase(minutes)} ${project.kind === 'stage-play' ? 'on stage' : project.kind === 'radio-play' ? 'on air' : 'on screen'}, at a page a minute.`}
      </p>
      {speeches.length ? (
        <ol className="chapter-bars">
          {speeches.map((s) => (
            <li key={s.name}>
              <span className="chapter-name">
                <span>{character(s.name)?.name ?? s.name}</span>
                <span className="chapter-label">{formatNumber(s.speeches)} {s.speeches === 1 ? 'speech' : 'speeches'}</span>
              </span>
              <Bar value={s.words} max={most} label={formatWords(s.words)} />
              <span className="num">{formatNumber(s.words)}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="stat-note">Nobody speaks yet.</p>
      )}
      {project.musical && songs.singers.length ? (
        <>
          <h3 className="eyebrow" data-songs={songs.total}>Songs by character</h3>
          <ol className="chapter-bars">
            {songs.singers.map((s) => (
              <li key={s.name}>
                <span className="chapter-name"><span>{character(s.name)?.name ?? s.name}</span></span>
                <Bar value={s.songs} max={Math.max(1, ...songs.singers.map((x) => x.songs))} label={`${formatNumber(s.songs)} ${s.songs === 1 ? 'song' : 'songs'}`} />
                <span className="num">{formatNumber(s.songs)}</span>
              </li>
            ))}
          </ol>
          <p className="stat-note">Who sings in each song: the characters with lyrics between its “Song:” line and the next song or blackout.</p>
        </>
      ) : null}
    </section>
  );
}
